import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";

import { usePreferences } from "@components/preferences";
import { useModal } from "@root/client/modal";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { ProfileNavigation } from "@common/profileNavigation";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveProjectPortfolios,
  useRetrieveStrategicAxes,
  useExportData,
  useAuthentication,
  useRetrieveProjectListingConfiguration,
  useRetrieveSecondaryCriteriaTemplate,
  useProjectStatusesRetrieval,
} from "@planzone/view-models";
import {
  ExportsDataTypes,
  ExportsFormats,
  Identifiable,
  Project,
  ProjectPortfolio,
  Filter,
} from "@planzone/dto";
import { events } from "@planzone/events";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { useRoles } from "@scenes/safeguard";
import { FilterMenu } from "@common/filterMenu";
import { filter } from "@common/filter";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { InfiniteScroll } from "@common/infiniteScroll";
import { useMessage } from "@components/useMessage";
import { Props } from "./types";
import { ProjectPortfolios } from "./projectPortfolios";
import { ExportButton } from "./exportButton";
import styles from "./styles.scss";
function filterProjects(Projects: Identifiable<Project>[], f: Filter) {
  return filter(Projects, f, (project) => ({ project }));
}

function filterPortfolio(
  projectPortfolio: Identifiable<ProjectPortfolio>[],
  f: Filter,
) {
  return filter(projectPortfolio, f, (projectPortfolio) => ({
    projectPortfolio,
  }));
}

function ProjectPortfoliosPage(props: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { exportData } = useExportData();
  const { account } = useAuthentication();
  const { state, edit } = usePreferences();
  const { configuration } = useRetrieveProjectListingConfiguration(planzone);
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const roles = useRoles({});
  const {
    isRequestPending: isStrategicAxesRequestPending,
    isRequestSuccess: isStrategicAxesRequestSuccess,
    isRequestFailure: isStrategicAxesRequestFailure,
    strategicAxes,
    retrieveStrategicAxes,
  } = useRetrieveStrategicAxes();
  let { projectPortfoliosFilter: filter, projectListingColumnSizes } = state;
  const { statuses } = useProjectStatusesRetrieval();

  if (!filter.projectStatuses || filter.projectStatuses.length === 0) {
    filter = {
      ...filter,
      projectStatuses: statuses.filter(
        (status) => status.label !== "pz_closed",
      ),
    };
  }
  const setFilter = (nextFilter: Filter) =>
    edit({ ...state, projectPortfoliosFilter: nextFilter });
  const { portfolios, retrieveProjectPortfolios } =
    useRetrieveProjectPortfolios();
  const { pushView } = useModal();
  const [columnSizes, setColumnSizes] = React.useState(
    projectListingColumnSizes,
  );

  React.useEffect(() => {
    retrieveProjectPortfolios(planzone);
  }, []);

  React.useEffect(() => {
    !isStrategicAxesRequestPending &&
      !isStrategicAxesRequestSuccess &&
      !isStrategicAxesRequestFailure &&
      retrieveStrategicAxes(planzone);
  }, []);

  useMessage(
    ({ type }) =>
      (type === events.assignProjectToPortfolioSucceeded ||
        type === events.createProjectStrategicAxisSucceeded ||
        type === events.editProjectStrategicAxisSucceeded) &&
      retrieveProjectPortfolios(planzone),
  );

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.projectPortfoliosPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("projectPortfoliosPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.main}>
          <div className={styles.toolbar}>
            <div>
              <FilterMenu
                filter={filter}
                placeholder={t("projectPortfoliosPage.filterPlaceholder")}
                setFilter={setFilter}
                views={[
                  "zones",
                  "projects",
                  "projectStatuses",
                  "projectsTags",
                  "projectStatuses",
                  "projectPortfolios",
                ]}
                openedViews={["zones"]}
              />
            </div>
            <div className={styles.row}>
              <div
                className={styles.contextualMenuIcon}
                onClick={() =>
                  pushView({
                    key: UseCases.retrieveProjectListingConfiguration,
                    data: undefined,
                  })
                }
              >
                <CogWheelIcon className={styles.wbsConfigurationIcon} />
              </div>
              <button
                className={styles.button}
                onClick={() =>
                  pushView({
                    key: UseCases.createProjectPortfolio,
                    data: undefined,
                  })
                }
              >
                <div className={styles.iconBox}>+</div>
                <p>{t("projectPortfoliosPage.createProjectPortfolio")}</p>
              </button>
            </div>
          </div>
          <div className={styles.wrapper}>
            <InfiniteScroll className={styles.projectPortfolios} canFetchOnIdle>
              {() => (
                <ProjectPortfolios
                  projectPortfolios={portfolios}
                  filter={filter}
                  columnSizes={columnSizes}
                  resizeCol={(key, size) =>
                    setColumnSizes((sizes) => ({
                      ...sizes,
                      [key]: size,
                    }))
                  }
                  resizeDone={() =>
                    edit({
                      ...state,
                      projectListingColumnSizes: columnSizes,
                    })
                  }
                />
              )}
            </InfiniteScroll>
          </div>
        </div>
        <div className={styles.exportButton}>
          <ExportButton
            export={() => {
              exportData({
                fileName: planzone.name,
                author: account,
                data: {
                  type: ExportsDataTypes.PROJECT_PORTFOLIOS_REPORT,
                  data: {
                    planzone: planzone,
                    projectPortfolios: portfolios,
                    strategicAxis: strategicAxes,
                    roles: roles,
                    configuration: configuration,
                    template: template,
                    filter: filter,
                  },
                },
                format: ExportsFormats.EXCEL,
              });
            }}
          ></ExportButton>
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { ProjectPortfoliosPage };
